<template lang="pug">
  .pho-date-time-picker-wrapper(
    :data-test-id="PhoDateTimePickerAutomatedTestIds.PHO_DATE_TIME_PICKER_WRAPPER")
    v-menu(
      v-model="menu"
      :ref="`date.${$vnode.key}`"
      content-class="pho-date-time-picker"
      nudge-right="0"
      transition="scale-transition"
      min-width="290px"
      :offset-y="true"
      :disabled="disabled"
      :close-on-content-click="false"
    )
      template(v-slot:activator="{ on, attrs }")
        PhoValidationProvider(:name="name" :rules="rules")
          v-text-field(
            ref="textFieldRef"
            readonly
            v-bind="attrs"
            v-on="on"
            :dense="dense"
            :outlined="outlined"
            :value="formattedValue"
            :label="label"
            :placeholder="label"
            :clearable="clearable"
            :disabled="disabled"
            @click.stop="onShow"
            @click:clear="clear"
          )
      .pho-date-time-picker__pickers
        v-date-picker.date-time-picker__date-picker(
          v-model="lazyDate"
          :locale="$i18n.locale"
          :allowed-dates="allowedDates"
          :min="minimumDate"
          :data-test-id="PhoDateTimePickerAutomatedTestIds.PHO_DATE_PICKER"
          @input="onDateChanged"
        )
        v-time-picker.date-time-picker__time-picker(
          v-model="lazyTime"
          ref="timePickerRef"
          format="24hr"
          :disabled="isTimeDisabled"
          :locale="$i18n.locale"
          :allowed-hours="setAllowedHours"
          :allowed-minutes="setAllowedMinutes"
          :min="minimumTime"
          @input="onTimeChanged"
          @click:hour="setSelectedHour"
        )
      .pho-date-time-picker__action-bar.d-flex.justify-center.px-2.py-2
        v-btn(text @click="cancel") {{$t($lang.app.CANCEL)}}
        v-btn.ml-1(text color="success" :disabled="isApplyDisabled" :data-test-id="PhoDateTimePickerAutomatedTestIds.SAVE_BUTTON"
          @click="apply") {{$t($lang.app.OK)}}
</template>

<script>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import {
  formatDateTime, getHours, getMinutes, displayDateTimeFormatWithoutSecond,
} from '@/helpers/misc/date';
import { PhoDateTimePickerAutomatedTestIds } from '@/components/misc/form/constants';
import PhoValidationProvider from './PhoValidationProvider.vue';

export default {
  name: 'PhoDateTimePicker',
  components: {
    PhoValidationProvider,
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    displayFormat: {
      type: String,
      default() {
        return displayDateTimeFormatWithoutSecond;
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    allowedDates: {
      type: Function,
      default: null,
    },
    minDateTime: {
      type: String,
      default: null,
    },
    currentDate: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const lazyValue = ref('');

    watch(() => props.value, newValue => {
      lazyValue.value = formatDateTime(newValue);
    });

    const menu = ref(false);
    const isTimeDisabled = ref(true);
    const lazyDate = ref('');
    const lazyTime = ref('');
    const timePickerRef = ref(null);
    const currentHour = ref(getHours(props.currentDate));
    const currentMinute = ref(getMinutes(props.currentDate));
    const selectedHour = ref(null);
    const minimumDate = ref(null);
    const initialTime = ref(null);

    const onShow = () => {
      const [date, time] = (lazyValue.value || '').split(' ');
      lazyDate.value = date || '';
      lazyTime.value = time || '';

      // Solution of the problem of not arriving the hour selection at first opening after the minute selection
      if (timePickerRef.value) {
        timePickerRef.value.selectingHour = true;
      }
    };

    const formattedValue = computed(() => formatDateTime(props.value, props.displayFormat));

    const isApplyDisabled = computed(() => !(lazyDate.value && lazyTime.value));

    const setAllowedHours = val => {
      if (lazyDate.value === props.currentDate.substr(0, 10)) {
        return val >= currentHour.value;
      }

      return true;
    };

    const setAllowedMinutes = val => {
      if (lazyDate.value === props.currentDate.substr(0, 10) && selectedHour.value === currentHour.value) {
        return val >= currentMinute.value;
      }

      return true;
    };

    const setSelectedHour = val => { selectedHour.value = val; };

    const onDateChanged = newValue => {
      lazyDate.value = newValue;
      isTimeDisabled.value = false;

      setAllowedHours(currentHour.value);
      setAllowedMinutes(currentMinute.value);
    };

    const onTimeChanged = newValue => {
      lazyTime.value = newValue;
    };

    const clear = () => {
      lazyValue.value = '';
      emit('input', undefined);
    };

    const cancel = () => {
      isTimeDisabled.value = true;
      menu.value = false;
    };

    const apply = () => {
      emit('input', new Date(`${lazyDate.value} ${lazyTime.value}`).toISOString());
      menu.value = false;
    };

    watch(() => props.currentDate, newValue => {
      currentHour.value = getHours(newValue);
      currentMinute.value = getMinutes(newValue);
      setAllowedHours(currentHour.value);
      setAllowedMinutes(currentMinute.value);
      onTimeChanged(`${currentHour.value}:${currentMinute.value}`);
    });

    const minimumTime = computed(() => {
      if (minimumDate.value !== lazyDate.value && props.minDateTime !== null) {
        return null;
      }

      return initialTime.value;
    });

    onMounted(() => {
      lazyValue.value = formatDateTime(props.value);
      const date = props.minDateTime ? props.minDateTime.split(' ') : null;
      minimumDate.value = date ? date[0] : null;
      initialTime.value = date ? date[1] : null;
    });

    watch(() => props.minDateTime, () => {
      const date = props.minDateTime ? props.minDateTime.split(' ') : null;
      minimumDate.value = date ? date[0] : null;
      initialTime.value = date ? date[1] : null;
    });

    return {
      lazyValue,
      lazyDate,
      lazyTime,
      timePickerRef,
      onShow,
      formattedValue,
      isApplyDisabled,
      menu,
      isTimeDisabled,
      onDateChanged,
      onTimeChanged,
      clear,
      cancel,
      apply,
      setAllowedHours,
      setAllowedMinutes,
      selectedHour,
      setSelectedHour,
      minimumTime,
      minimumDate,
      initialTime,
      PhoDateTimePickerAutomatedTestIds,
    };
  },
};
</script>

<style lang="scss">
.pho-date-time-picker {
  z-index: var(--z-index-overlay) !important;

  .v-picker {
    &__title {
      &.primary {
        height: 100px;
      }
    }
  }

  &__pickers {
    display: flex;
  }

  &__date-picker {
    position: relative;

    .v-picker {
      &__title {
        &.primary {
          border-top-right-radius: 0 !important;
        }
      }
    }
  }

  &__time-picker {
    position: relative;

    .v-picker {
      &__title {
        &.primary {
          border-top-left-radius: 0 !important;
        }
      }
    }
  }

  &__action-bar {
    position: relative;
    background-color: var(--color-ui-02);
  }
}
</style>
